import { observer } from 'mobx-react-lite'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'

import { AccountToggle } from '../AccountToggle/AccountToggle'
import Styles from './BrandKitAccountsSelect.module.scss'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ConnectionNeededItem } from '../ConnectionNeeded/ConnectionNeededItem/ConnectionNeededItem'
import { useConnectAccount } from 'components/BrandKitCreationModal/useConnectAccount'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { uniqBy } from 'lodash'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { Tooltip } from 'components/Tooltip/Tooltip'

export const ConnectedBrandKitAccountsSelect = observer(() => {
  const store = usePostPreviewStore()
  const previewCtx = usePostPreviewContext()
  const { data } = useBrandKit(store.selectedBrandKitId)
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource()

  const onConnect = async (accounts: ExternalIntegration[]) => {
    if (!data?.brand_kit || accounts.length === 0) return

    await createBrandKitSource({
      brandKitId: data.brand_kit.id,
      source: { type: 'ExternalIntegration', id: accounts[0].id },
    })

    store.setSelectedAccounts(uniqBy([...store.selectedAccounts, ...accounts], 'id'))
    store.addChannel(accounts[0].provider)
  }

  const connect = useConnectAccount({ onConnect })

  const handleSelect = (account: ExternalIntegration) => {
    if (store.isPublishing) return
    const exists = store.selectedAccounts.find((a) => a.id === account.id)
    store.setSelectedAccounts(
      exists ? store.selectedAccounts.filter((a) => a.id !== account.id) : [...store.selectedAccounts, account],
    )
  }

  if (!data?.brand_kit) return null

  const enabled = data.brand_kit.external_integrations
    .filter((a) => store.selectedChannels.has(a.provider))
    .sort((a, b) => (a.provider === providerFromChannelType[store.channel || DesignChannel.InstagramPost] ? -1 : 1))

  const hasIntegrationForChannel =
    store.channel &&
    data.brand_kit.external_integrations.some(
      (a) => a.provider === providerFromChannelType[store.channel as DesignChannel],
    )

  const provider = providerFromChannelType[store.channel as DesignChannel]

  const renderAccountToggle = (account: ExternalIntegration) => {
    const isInstagram = account.provider === ExternalIntegrationEnum.Instagram
    const noMedia = !previewCtx?.scheduledPost?.media_objects?.length
    const isDisabled =
      isInstagram &&
      noMedia &&
      providerFromChannelType[store.channel as DesignChannel] !== ExternalIntegrationEnum.Instagram

    const toggle = (
      <AccountToggle
        key={account.id}
        account={account}
        isSelected={!!store.selectedAccounts.find((a) => a.id === account.id)}
        onChange={() => handleSelect(account)}
        isDisabled={isDisabled}
      />
    )

    return isDisabled ? (
      <Tooltip key={account.id} label="Can't post on Instagram without image">
        {toggle}
      </Tooltip>
    ) : (
      toggle
    )
  }

  if (!hasIntegrationForChannel && provider)
    return (
      <div className={Styles.accounts}>
        <ConnectionNeededItem provider={provider} onConnect={() => connect(provider)} />
        {enabled.map(renderAccountToggle)}
      </div>
    )

  return <div className={Styles.accounts}>{enabled.map(renderAccountToggle)}</div>
})
